import {
	isAuditedAuthEvent,
	resolveAuthEventSubject,
} from "../domain/catalog.ts";
import type {
	AthenaAuthActor,
	AthenaAuthRequestContext,
} from "../domain/context.ts";
import type { AthenaAuthAuditWriter } from "../observability/audit.ts";
import type { AthenaAuthAuditEntry } from "../observability/types.ts";
import { currentAuthTrace } from "../observability/traces.ts";
import type { AthenaAuthImplementedDomainEvent } from "./events.ts";
import {
	createAuthHookContext,
	runAuthAfterHooks,
	runAuthBeforeHooks,
} from "./runner.ts";
import type {
	AthenaAuthMutationScope,
	AuthMutationTransaction,
} from "./scope.ts";
import type {
	AthenaAuthHookEventPayloads,
	AthenaAuthHooks,
} from "./types.ts";

export type AuthDomainMutate = <
	E extends AthenaAuthImplementedDomainEvent,
	TResult,
>(
	input: Omit<
		ExecuteAuthMutationOptions<E, TResult>,
		"auditWriter" | "hooks" | "transaction"
	>,
) => Promise<TResult>;

export interface AthenaAuthMutationContext {
	actor: AthenaAuthActor;
	request: AthenaAuthRequestContext;
	traceId: string;
}

export interface AthenaAuthSecondaryEvent<
	E extends AthenaAuthImplementedDomainEvent = AthenaAuthImplementedDomainEvent,
> {
	event: E;
	input: AthenaAuthHookEventPayloads[E]["input"];
	previous?: AthenaAuthHookEventPayloads[E]["previous"];
	result: AthenaAuthHookEventPayloads[E]["result"];
}

export interface ExecuteAuthMutationOptions<
	E extends AthenaAuthImplementedDomainEvent,
	TResult,
> {
	auditWriter?: AthenaAuthAuditWriter;
	context: AthenaAuthMutationContext;
	event: E;
	execute: (scope: AthenaAuthMutationScope) => Promise<TResult>;
	hooks?: AthenaAuthHooks;
	input: AthenaAuthHookEventPayloads[E]["input"];
	previous?: () => Promise<
		AthenaAuthHookEventPayloads[E]["previous"] | undefined
	>;
	resultOf: (result: TResult) => AthenaAuthHookEventPayloads[E]["result"];
	secondaryEvents?: (result: TResult) => readonly AthenaAuthSecondaryEvent[];
	transaction: AuthMutationTransaction;
}

async function timePhase<T>(
	name: "after_hooks" | "authorize" | "before_hooks" | "transaction" | "validate",
	fn: () => Promise<T>,
): Promise<T> {
	const span = currentAuthTrace()?.phase(name);
	try {
		return await fn();
	} finally {
		span?.finish();
	}
}

export async function executeAuthMutation<
	E extends AthenaAuthImplementedDomainEvent,
	TResult,
>(options: ExecuteAuthMutationOptions<E, TResult>): Promise<TResult> {
	const envelope = createAuthHookContext(options.context);
	currentAuthTrace()?.setActor(options.context.actor);
	currentAuthTrace()?.setEvent(options.event, envelope.eventId);
	const previous = options.previous ? await options.previous() : undefined;
	await timePhase("before_hooks", () =>
		runAuthBeforeHooks(options.hooks, {
			...envelope,
			event: options.event,
			input: options.input,
			previous,
		}),
	);
	let secondaryWork: Array<{
		envelope: ReturnType<typeof createAuthHookContext>;
		extra: AthenaAuthSecondaryEvent;
	}> = [];
	const result = await timePhase("transaction", () =>
		options.transaction(async (scope) => {
			const executed = await options.execute(scope);
			const secondary = options.secondaryEvents?.(executed) ?? [];
			secondaryWork = secondary.map((extra) => ({
				envelope: createAuthHookContext(options.context),
				extra,
			}));
			const writeAudit = async (entry: AthenaAuthAuditEntry) => {
				if (options.auditWriter) {
					await options.auditWriter.write(scope, entry);
					return;
				}
				await scope.persistAudit?.(entry);
			};
			if (options.auditWriter || scope.persistAudit) {
				const primaryResult = options.resultOf(executed);
				if (isAuditedAuthEvent(options.event)) {
					await writeAudit({
						actor: options.context.actor,
						event: options.event,
						eventId: envelope.eventId,
						id: crypto.randomUUID(),
						organizationId: options.context.actor.organizationId,
						outcome: "success",
						previous,
						request: {
							ipAddress: options.context.request.ipAddress,
							userAgent: options.context.request.userAgent,
						},
						result: primaryResult,
						subject: resolveAuthEventSubject(options.event, primaryResult),
						traceId: options.context.traceId,
					});
				}
				for (const { envelope: extraEnvelope, extra } of secondaryWork) {
					if (!isAuditedAuthEvent(extra.event)) {
						continue;
					}
					await writeAudit({
						actor: options.context.actor,
						event: extra.event,
						eventId: extraEnvelope.eventId,
						id: crypto.randomUUID(),
						organizationId: options.context.actor.organizationId,
						outcome: "success",
						previous: extra.previous,
						request: {
							ipAddress: options.context.request.ipAddress,
							userAgent: options.context.request.userAgent,
						},
						result: extra.result,
						subject: resolveAuthEventSubject(extra.event, extra.result),
						traceId: options.context.traceId,
					});
				}
			}
			return executed;
		}),
	);
	await timePhase("after_hooks", () =>
		runAuthAfterHooks(options.hooks, {
			...envelope,
			event: options.event,
			input: options.input,
			previous,
			result: options.resultOf(result),
		}),
	);
	for (const { envelope: extraEnvelope, extra } of secondaryWork) {
		await runAuthAfterHooks(options.hooks, {
			...extraEnvelope,
			event: extra.event,
			input: extra.input,
			previous: extra.previous,
			result: extra.result,
		} as never);
	}
	return result;
}
